import type z from "zod";
import type { SavedFilterType } from "../../schemas/SavedFiltersSchemas.ts";
import {
  fetchAndCollate,
  type ResourceAdapterType,
  type ResponseErrorType,
} from "../../util/index.ts";
import { restAPI } from "../api.ts";

export type SavedFiltersAdapterType<T extends z.ZodTypeAny> = Pick<
  ResourceAdapterType<SavedFilterType<T>>,
  "getList" | "updateOne" | "createOne" | "deleteOne"
>;

export const SavedFiltersAdapter = <T extends z.ZodTypeAny>({
  endpoint,
}: {
  endpoint: string;
}): SavedFiltersAdapterType<T> => ({
  getList: ({
    sortBy,
    sortDirection,
    page,
    pageSize,
    filters: { name, status, createdBy, tag } = {},
  }) =>
    fetchAndCollate<SavedFilterType<T>>(
      ({ page, pageSize }) =>
        restAPI.nodeAPI.GET({
          endpoint,
          query: {
            page,
            page_size: pageSize,
            ...(sortBy ? { sort_by: sortBy } : {}),
            ...(sortDirection ? { sort_direction: sortDirection } : {}),
            ...(status ? { status } : {}),
            ...(tag ? { tag } : {}),
            ...(name ? { name } : {}),
            ...(createdBy ? { created_by: createdBy } : {}),
          },
        }),
      page,
      pageSize
    ),
  createOne: ({ data }) =>
    restAPI.nodeAPI
      .POST<SavedFilterType<T>>({
        endpoint,
        body: data,
      })
      .then((resp) => ({ data: resp })),
  updateOne: ({ id, data }) =>
    restAPI.nodeAPI
      .PUT<SavedFilterType<T>>({
        endpoint: [endpoint, id].join("/"),
        body: data,
      })
      .then((resp) => ({ data: resp })),
  deleteOne: ({ id }) =>
    restAPI.nodeAPI
      .DELETE<SavedFilterType<T>>({
        endpoint: [endpoint, id].join("/"),
      })
      .then(() => ({ success: true }))
      .catch((resp: ResponseErrorType) => resp),
});
